import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  ChartBarIcon,
  UserGroupIcon,
  CpuChipIcon,
  ChatBubbleLeftIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const SystemAnalytics = ({ user }) => {
  const [timeRange, setTimeRange] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('usage');
  const [analytics, setAnalytics] = useState({});
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    // Mock analytics data - in real implementation, this would come from API
    const mockAnalytics = {
      overview: {
        totalUsers: 127,
        activeUsers: 89,
        totalSessions: 1247,
        avgSessionTime: '12.4 min',
        chatMessages: 15623,
        mediaUploads: 892,
        agentCalls: 3421,
        searchQueries: 2156
      },
      usageData: [
        { date: '2024-01-01', chat: 120, upload: 45, agents: 67, search: 34 },
        { date: '2024-01-02', chat: 135, upload: 52, agents: 71, search: 41 },
        { date: '2024-01-03', chat: 148, upload: 38, agents: 89, search: 29 },
        { date: '2024-01-04', chat: 167, upload: 64, agents: 92, search: 55 },
        { date: '2024-01-05', chat: 178, upload: 71, agents: 84, search: 48 },
        { date: '2024-01-06', chat: 145, upload: 42, agents: 76, search: 38 },
        { date: '2024-01-07', chat: 156, upload: 58, agents: 88, search: 52 }
      ],
      userActivity: [
        { hour: '00:00', users: 12, intensity: 0.1 },
        { hour: '01:00', users: 8, intensity: 0.05 },
        { hour: '02:00', users: 5, intensity: 0.03 },
        { hour: '03:00', users: 3, intensity: 0.02 },
        { hour: '04:00', users: 2, intensity: 0.01 },
        { hour: '05:00', users: 4, intensity: 0.03 },
        { hour: '06:00', users: 15, intensity: 0.15 },
        { hour: '07:00', users: 28, intensity: 0.28 },
        { hour: '08:00', users: 45, intensity: 0.45 },
        { hour: '09:00', users: 67, intensity: 0.67 },
        { hour: '10:00', users: 78, intensity: 0.78 },
        { hour: '11:00', users: 89, intensity: 0.89 },
        { hour: '12:00', users: 92, intensity: 0.92 },
        { hour: '13:00', users: 95, intensity: 0.95 },
        { hour: '14:00', users: 87, intensity: 0.87 },
        { hour: '15:00', users: 84, intensity: 0.84 },
        { hour: '16:00', users: 79, intensity: 0.79 },
        { hour: '17:00', users: 71, intensity: 0.71 },
        { hour: '18:00', users: 58, intensity: 0.58 },
        { hour: '19:00', users: 45, intensity: 0.45 },
        { hour: '20:00', users: 38, intensity: 0.38 },
        { hour: '21:00', users: 32, intensity: 0.32 },
        { hour: '22:00', users: 25, intensity: 0.25 },
        { hour: '23:00', users: 18, intensity: 0.18 }
      ],
      featureUsage: [
        { name: 'Chat', value: 42, color: '#3B82F6' },
        { name: 'Media Upload', value: 23, color: '#10B981' },
        { name: 'Agents', value: 18, color: '#8B5CF6' },
        { name: 'Memory Search', value: 17, color: '#EC4899' }
      ],
      performanceData: [
        { metric: 'Response Time', value: '1.2s', trend: 'down', change: '-15%' },
        { metric: 'Success Rate', value: '99.2%', trend: 'up', change: '+2.1%' },
        { metric: 'Error Rate', value: '0.8%', trend: 'down', change: '-45%' },
        { metric: 'Uptime', value: '99.9%', trend: 'stable', change: '0%' }
      ]
    };

    // Generate heatmap data for the week
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    const heatmap = days.map(day => 
      hours.map(hour => ({
        day,
        hour,
        value: Math.random() * 100,
        intensity: Math.random()
      }))
    ).flat();

    setAnalytics(mockAnalytics);
    setHeatmapData(heatmap);
    setLoading(false);
  };

  const getIntensityColor = (intensity) => {
    if (intensity < 0.2) return 'bg-blue-100';
    if (intensity < 0.4) return 'bg-blue-200';
    if (intensity < 0.6) return 'bg-blue-300';
    if (intensity < 0.8) return 'bg-blue-400';
    return 'bg-blue-500';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
      case 'down': return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
      default: return <div className="w-4 h-4 bg-gray-400 rounded-full"></div>;
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, change, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <div className="flex items-center mt-1">
              {getTrendIcon(trend)}
              <span className={`text-sm ml-1 ${
                trend === 'up' ? 'text-green-600' : 
                trend === 'down' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {change}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const UsageHeatmap = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Usage Heatmap - Activity by Hour and Day
      </h3>
      <div className="flex items-center mb-4">
        <span className="text-sm text-gray-600 mr-4">Activity Level:</span>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-100 rounded"></div>
          <span className="text-xs text-gray-500">Low</span>
          <div className="w-3 h-3 bg-blue-300 rounded"></div>
          <span className="text-xs text-gray-500">Medium</span>
          <div className="w-3 h-3 bg-blue-500 rounded"></div>
          <span className="text-xs text-gray-500">High</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <div className="grid grid-cols-25 gap-1 min-w-max">
          {/* Header row with hours */}
          <div></div>
          {Array.from({ length: 24 }, (_, i) => (
            <div key={i} className="text-xs text-gray-500 text-center w-6">
              {i.toString().padStart(2, '0')}
            </div>
          ))}
          
          {/* Data rows */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <React.Fragment key={day}>
              <div className="text-xs text-gray-500 py-1 pr-2">{day}</div>
              {Array.from({ length: 24 }, (_, hour) => {
                const dataPoint = heatmapData.find(d => d.day === day && d.hour === hour);
                return (
                  <div
                    key={`${day}-${hour}`}
                    className={`w-6 h-6 rounded cursor-pointer transition-all hover:scale-110 ${
                      getIntensityColor(dataPoint?.intensity || 0)
                    }`}
                    title={`${day} ${hour}:00 - ${Math.round((dataPoint?.value || 0))} activities`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Analytics</h2>
          <p className="text-gray-600">Usage patterns, performance metrics, and insights</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 3 Months</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={analytics.overview?.totalUsers}
          icon={UserGroupIcon}
          color="blue"
        />
        <StatCard
          title="Active Sessions"
          value={analytics.overview?.totalSessions}
          icon={ChartBarIcon}
          color="green"
        />
        <StatCard
          title="Chat Messages"
          value={analytics.overview?.chatMessages?.toLocaleString()}
          icon={ChatBubbleLeftIcon}
          color="purple"
        />
        <StatCard
          title="Agent Calls"
          value={analytics.overview?.agentCalls?.toLocaleString()}
          icon={CpuChipIcon}
          color="pink"
        />
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {analytics.performanceData?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
              <div className="text-sm text-gray-600">{metric.metric}</div>
              <div className="flex items-center justify-center mt-1">
                {getTrendIcon(metric.trend)}
                <span className={`text-sm ml-1 ${
                  metric.trend === 'up' ? 'text-green-600' : 
                  metric.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {metric.change}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Trends Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analytics.usageData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="chat" stroke="#3B82F6" strokeWidth={2} />
            <Line type="monotone" dataKey="upload" stroke="#10B981" strokeWidth={2} />
            <Line type="monotone" dataKey="agents" stroke="#8B5CF6" strokeWidth={2} />
            <Line type="monotone" dataKey="search" stroke="#EC4899" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature Usage Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Usage Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.featureUsage}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {analytics.featureUsage?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Hourly Activity Pattern</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={analytics.userActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Usage Heatmap */}
      <UsageHeatmap />

      {/* Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <FireIcon className="w-5 h-5 mr-2 text-orange-500" />
          Key Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-white p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Peak Usage</h4>
            <p className="text-gray-700">
              Highest activity occurs between 12-2 PM, with chat being the most popular feature.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Growth Trend</h4>
            <p className="text-gray-700">
              User engagement has increased 23% over the past week, driven by agent adoption.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Performance</h4>
            <p className="text-gray-700">
              System response times have improved 15% with the new optimization updates.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">User Behavior</h4>
            <p className="text-gray-700">
              Average session length has increased to 12.4 minutes, indicating higher engagement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAnalytics;